import { Divider, Grid, Pagination, Stack, useTheme } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { answerColorToThemeColor } from "components/Evaluation/Utils";
import React, { useState } from "react";
import { SuccessfulResult } from "./SuccessfulResult";
import { TestLLMClassifierOnConversationResult } from "models/LLMs/Testing/TestLLMClassifierOnConversationResult";
import { ResultBadge } from "../ResultBadge";
import { getFriendlyId } from "../utils";
import { AnswerColor } from "components/UI/AcxAnswerSelector";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";

interface SuccessfulResultsSectionProps {
    results: TestLLMClassifierOnConversationResult[];
    label: string;
    subtitle?: string;
}

export function SuccessfulResultsSection({
    results,
    label,
    subtitle,
}: SuccessfulResultsSectionProps) {
    const theme = useTheme();
    const [pageNumber, setPageNumber] = useState(1);

    const start =
        (pageNumber - 1) * LLMClassifierBuilderStore.TestingPerSectionPageSize;
    const end = Math.min(
        start + LLMClassifierBuilderStore.TestingPerSectionPageSize,
        results.length,
    );
    const resultsToShow = results.slice(start, end);

    const pageCount = Math.ceil(
        results.length / LLMClassifierBuilderStore.TestingPerSectionPageSize,
    );

    const sectionId = getFriendlyId(label);

    const hasMoreThanOnePage = pageCount > 1;

    return (
        <TitledSection
            id={sectionId}
            title={
                <Stack direction="row" columnGap={2} alignItems="center">
                    <ResultBadge color={AnswerColor.BLUE}>
                        {label} (
                        {hasMoreThanOnePage && `${start + 1}-${end} of `}
                        {results.length})
                    </ResultBadge>
                    {hasMoreThanOnePage && (
                        <Pagination
                            count={pageCount}
                            page={pageNumber}
                            onChange={(_, value) => setPageNumber(value)}
                            size="small"
                            showFirstButton
                            showLastButton
                        />
                    )}
                </Stack>
            }
            variant="bordered"
            collapsible
            sx={{
                borderLeft: `4px solid ${
                    answerColorToThemeColor(theme, AnswerColor.BLUE).borderColor
                }`,
            }}
            subtitle={subtitle}
        >
            {results.length > 0 && (
                <Grid container direction="column" flexWrap="nowrap" rowGap={2}>
                    {resultsToShow.map((result, number) => (
                        <React.Fragment key={result.conversation.id}>
                            <SuccessfulResult
                                result={result}
                                number={start + number}
                            />
                            {number !== resultsToShow.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </Grid>
            )}
            {hasMoreThanOnePage && (
                <Pagination
                    count={pageCount}
                    page={pageNumber}
                    onChange={(_, value) => {
                        setPageNumber(value);
                        document
                            .querySelector(`#${sectionId}`)
                            ?.scrollIntoView({
                                behavior: "smooth",
                            });
                    }}
                    size="small"
                />
            )}
        </TitledSection>
    );
}
